import React, { useState, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Pressable } from 'react-native';
import { ChevronDown, ChevronUp, X, Check } from 'lucide-react';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { COUNTRIES } from '../../constants/countries';

const WebMultiCountrySelect = ({ value = [], onChange, placeholder = 'Select Countries', error, label, noUSD }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const buttonRef = useRef(null);

  const openDropdown = useCallback(() => {
    if (buttonRef.current) {
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setDropdownPos({ top: y + height + 4, left: x, width });
        setIsOpen(true);
      });
    } else {
      setIsOpen(true);
    }
  }, []);

  const availableCountries = Object.values(COUNTRIES).filter(c => {
    if (noUSD && c.code === 'USD') return false;
    return true;
  });

  const isSelected = (code) => value.includes(code);

  const handleToggle = (code) => {
    if (isSelected(code)) {
      onChange(value.filter(c => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const handleRemove = (code) => {
    onChange(value.filter(c => c !== code));
  };

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}</Text>}
      <TouchableOpacity
        ref={buttonRef}
        style={[styles.selectButton, error && styles.selectButtonError, isOpen && styles.selectButtonActive]}
        onPress={() => isOpen ? setIsOpen(false) : openDropdown()}
        activeOpacity={0.7}
      >
        <Text style={[styles.selectButtonText, value.length === 0 && styles.selectButtonPlaceholder]}>
          {value.length > 0 ? `${value.length} ${value.length === 1 ? 'country' : 'countries'} selected` : placeholder}
        </Text>
        {isOpen ? (
          <ChevronUp size={16} color={webColors.textMuted} />
        ) : (
          <ChevronDown size={16} color={webColors.textMuted} />
        )}
      </TouchableOpacity>
      {error && <Text style={styles.errorText}>Please select at least one country</Text>}

      {isOpen && (
        <>
          <Pressable
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
            onPress={() => setIsOpen(false)}
          />
          <View style={[styles.dropdown, dropdownPos && {
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            right: undefined,
            zIndex: 9999,
          }]}>
            <ScrollView style={styles.optionsList} nestedScrollEnabled>
              {availableCountries.map((country) => (
                <TouchableOpacity
                  key={country.code}
                  style={[styles.option, isSelected(country.code) && styles.optionSelected]}
                  onPress={() => handleToggle(country.code)}
                >
                  <Text style={styles.optionFlag}>{country.flag}</Text>
                  <Text style={[styles.optionName, isSelected(country.code) && styles.optionNameSelected]}>
                    {country.name}
                  </Text>
                  {isSelected(country.code) && (
                    <View style={styles.checkIcon}>
                      <Check size={14} color="#ffffff" />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </>
      )}

      {value.length > 0 && (
        <View style={styles.chipsRow}>
          {value.map((code) => {
            const country = COUNTRIES[code];
            if (!country) return null;
            return (
              <View key={code} style={styles.chip}>
                <Text style={styles.chipText}>{country.flag} {country.name}</Text>
                <TouchableOpacity onPress={() => handleRemove(code)} style={styles.chipRemove}>
                  <X size={12} color={webColors.textMuted} />
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 9999 },
  label: { fontSize: 15, fontWeight: '700', color: webColors.textPrimary, marginBottom: 6 },
  selectButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: webColors.backgroundAlt, borderWidth: 1, borderColor: webColors.border,
    borderRadius: webBorderRadius.medium, padding: 14, paddingHorizontal: 16,
  },
  selectButtonActive: { borderColor: webColors.primary },
  selectButtonError: { borderColor: webColors.error },
  selectButtonText: { fontSize: 14, color: webColors.textPrimary },
  selectButtonPlaceholder: { color: webColors.textMuted },
  errorText: { fontSize: 12, color: webColors.error, marginTop: 4 },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
    backgroundColor: webColors.cardBackground, borderWidth: 1, borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12,
    elevation: 5, zIndex: 100,
  },
  optionsList: { maxHeight: 200 },
  option: { flexDirection: 'row', alignItems: 'center', padding: 12, paddingHorizontal: 16, gap: 10 },
  optionSelected: { backgroundColor: webColors.primaryLight },
  optionFlag: { fontSize: 18 },
  optionName: { flex: 1, fontSize: 14, color: webColors.textPrimary },
  optionNameSelected: { color: webColors.primary, fontWeight: '600' },
  checkIcon: {
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: webColors.primary, alignItems: 'center', justifyContent: 'center',
  },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  chip: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: webColors.backgroundAlt, borderWidth: 1, borderColor: webColors.border,
    borderRadius: webBorderRadius.pill, paddingVertical: 6, paddingLeft: 12, paddingRight: 8, gap: 6,
  },
  chipText: { fontSize: 13, color: webColors.textPrimary },
  chipRemove: { padding: 2 },
});

export default WebMultiCountrySelect;
